import * as React from 'react';
import {tokens} from '@packages/utilities/styled';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {STACK_GAP} from '../Stack.styles';

import type {StackProps} from '../_types';
import {useView} from '../../View/hooks';

// The spacing override object has the shape { _override: :string }, so we'll check if our input is an object and check for
// an _override key/value in order to determine if we're dealing with an override object.
const isSpacingOverride = (value: StackProps['spacing']) => {
  return typeof value === 'object' && Boolean(value._override);
};

export const useStack = ({
  align,
  direction,
  isFullHeight,
  isFullWidth: isFullWidthProp = true,
  justify,
  shouldWrap,
  spacing = 2,
  ...nonStackProps
}: StackProps) => {
  const viewProps = useView(nonStackProps);

  // Warn developers of weird wrapping behavior.
  const showWrapWarning = Boolean(direction === 'column' && shouldWrap);
  useDevAlert(
    showWrapWarning,
    `Do not use a VStack with shouldWrap. Your stack can potentially overflow its parent's width. See: https://stackoverflow.com/a/33899301`
  );

  // Warning for overriding spacing
  useDevAlert(
    typeof spacing !== 'number' && !isSpacingOverride(spacing),
    'To override the spacing value, you must supply an object of shape { _override: :string }. If you find yourself doing this, please contact the Design System team.'
  );

  const gap = React.useMemo(() => tokens.space.getValue(spacing), [spacing]);

  const isFullWidth = viewProps.width ? false : isFullWidthProp;

  const newStyle = React.useMemo(
    () => ({
      ...viewProps.style,
      [STACK_GAP]: gap,
    }),
    [viewProps.style, gap]
  );

  const shouldSetAutoHeight = !viewProps.height && !isFullHeight;

  return React.useMemo(
    () => ({
      ...viewProps,
      align,
      direction,
      justify,
      isFullHeight,
      isFullWidth,
      style: newStyle,
      shouldSetAutoHeight,
      shouldWrap,
    }),
    [
      align,
      direction,
      justify,
      isFullWidth,
      isFullHeight,
      newStyle,
      viewProps,
      shouldSetAutoHeight,
      shouldWrap,
    ]
  );
};
